import { useQueryClient } from '@tanstack/react-query';
import useTranslation from 'next-translate/useTranslation';
import { memo, useEffect } from 'react';

import { WishlistService } from '@core/api';
import { Box, ButtonIcon } from '@core/component';
import { Translate } from '@core/constant';
import {
  useContextRedirectAfterSignInRoute,
  useContextRouting,
  useContextToast,
  useContextUser,
  useContextUtil,
} from '@core/context';
import {
  useLanguage,
  useModalProductAlert,
  useMutationApi,
  useParamsQueriesKeys,
  useQueryApi,
} from '@core/hook';
import { ColorProps, EColor, EColorName, EIcon, ERouting, EZIndexName } from '@core/type';
import { EQueryKey, ProductBlockEntity, WishlistResponseEntity } from '@core/type/api';

import { ModalAddProductAlert, ModalUpdateProductAlert } from '../modal';
import { ProductTriggerAction } from './interface-product';

const ProductAlertActionsContainer = ({ ...productProps }: Omit<ProductBlockEntity, 'medias'>) => {
  const { id } = productProps;
  const queryClient = useQueryClient();

  const { axiosInstance } = useContextUtil();
  const { language } = useLanguage();
  const { isLogged } = useContextUser();
  const { addToast } = useContextToast();
  const { setRoute, keyRoute, query, setQuery } = useContextRouting();
  const { setRoute: setRedirectAfterSignInRoute, setQuery: setRedirectAfterSignInQuery } =
    useContextRedirectAfterSignInRoute();

  const { t: tCommon } = useTranslation(Translate.common.COMMON);

  const { addingProps, updatingProps, isUpdatingModal, onAlertButton } =
    useModalProductAlert(productProps);

  const { data: wishlistData, isLoading: isGettingWishList } = useQueryApi<WishlistResponseEntity>(
    EQueryKey.WISHLIST,
  );
  const queries = useParamsQueriesKeys(EQueryKey.WISHLIST);

  const isActiveFavorite = wishlistData?.products?.some(({ id: wishlistId }) => id === wishlistId);

  const { mutate: addToWishlist, isLoading: isAddingToWishList } = useMutationApi(
    async (productId: number) =>
      WishlistService.addProductToWishlist(axiosInstance, language, productId),
    {
      onSuccess: () => {
        queryClient.setQueryData(
          queries,
          (data: WishlistResponseEntity): WishlistResponseEntity => {
            return { products: data.products.concat(productProps) };
          },
        );
      },
    },
  );

  const { mutate: removeFromWishlist, isLoading: isRemovingFromWishList } = useMutationApi(
    async (productId: number) =>
      WishlistService.deleteProductFromWishList(axiosInstance, language, productId),
    {
      onSuccess: (_, productId: number) => {
        queryClient.setQueryData(
          queries,
          (data: WishlistResponseEntity): WishlistResponseEntity => {
            return { products: data.products.filter((product) => product.id !== productId) };
          },
        );
      },
    },
  );

  const redirectToSignIn = (trigger: ProductTriggerAction, errorKey: string) => {
    setRedirectAfterSignInRoute(keyRoute);
    setRedirectAfterSignInQuery({ ...query, trigger });
    setRoute(ERouting.SIGN_IN);
    addToast({
      title: tCommon('toast.warning'),
      description: tCommon(errorKey),
      status: EColor.WARNING,
    });
  };

  const handleWishlistButton = () => {
    if (!isLogged) {
      redirectToSignIn(ProductTriggerAction.WISHLIST, 'addWishlist.description');
      return;
    }

    isActiveFavorite ? removeFromWishlist(id) : addToWishlist(id);
  };

  const handleProductAlertButton = () => {
    if (!isLogged) {
      redirectToSignIn(ProductTriggerAction.ALERT, 'userMustLogged.description');
      return;
    }

    onAlertButton();
  };

  useEffect(() => {
    if (query.trigger) {
      const { trigger, ...restQuery } = query;

      if (trigger === ProductTriggerAction.WISHLIST) handleWishlistButton();
      if (trigger === ProductTriggerAction.ALERT) handleProductAlertButton();

      setQuery(restQuery);
    }
  }, [query]);

  const iconHoverColor: ColorProps = { commonType: EColor.BLUE, intensity: EColor.A800 };

  return (
    <>
      <Box
        zIndex={EZIndexName.OVER_DEFAULT}
        display="flex"
        gap="24px"
        marginTop={16}
        marginBottom={24}
      >
        <ButtonIcon
          iconProps={{
            type: EIcon.BELL,
            width: 20,
            height: 20,
            stroke: EColorName.EPIC_BLUE,
          }}
          backgroundColor={EColor.SECONDARY}
          hoverFill={iconHoverColor}
          hoverStroke={iconHoverColor}
          padding="10px"
          height={40}
          borderRadius={20}
          onClick={handleProductAlertButton}
        />
        <ButtonIcon
          iconProps={{
            type: EIcon.HEART_SHARP,
            width: 20,
            height: 20,
            stroke: isLogged && isActiveFavorite ? EColorName.ELECTRIC_BLUE : EColorName.EPIC_BLUE,
            color:
              isLogged && isActiveFavorite
                ? iconHoverColor
                : { commonType: EColor.WHITE, intensity: EColor.A900 },
          }}
          backgroundColor={EColor.SECONDARY}
          hoverFill={iconHoverColor}
          hoverStroke={iconHoverColor}
          disabled={isLogged && (isGettingWishList || isAddingToWishList || isRemovingFromWishList)}
          padding="10px"
          height={40}
          borderRadius={20}
          onClick={handleWishlistButton}
        />
      </Box>
      {isUpdatingModal ? (
        <ModalUpdateProductAlert {...updatingProps} />
      ) : (
        <ModalAddProductAlert {...addingProps} />
      )}
    </>
  );
};

export const ProductAlertActions = memo(ProductAlertActionsContainer);
